/**
 * Page demos — Layer 3. Real CSS-driven scenes that go through the
 * compileHtml() pipeline. These scenes look like actual web pages and
 * exercise the full CSS subset: flex layout, hover states, animations,
 * filters, transforms, mix-blend-mode, gradient backgrounds, etc.
 */

import { createScene, type Scene } from "../sceneGraph";
import { compileHtml } from "../cssIngest";

/** A landing-page hero with gradient, headline, subtext, CTA button,
 *  animated entrance via CSS keyframes, hover state on the button.
 *
 *  Layer 7 verification: the `.blurred` class triggers a per-node CSS
 *  `filter: blur(8px)` that flows through the compile pipeline into the
 *  instance buffer (attr 10/11), so the affected element renders with the
 *  per-node filter applied while siblings stay sharp. */
export function sceneHero(w: number, h: number): Scene {
  return compileHtml(`
    <style>
      .blurred { filter: blur(8px); }
    </style>
    <div style="
      width: ${w}px;
      height: ${h}px;
      background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
    ">
      <div style="
        background: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.10);
        border-radius: 24px;
        padding: 40px 32px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        max-width: 480px;
        width: 100%;
        box-shadow: 0 24px 48px rgba(0,0,0,0.4), 0 8px 16px rgba(0,0,0,0.3);
        backdrop-filter: blur(12px);
      ">
        <div style="
          width: 64px; height: 64px;
          border-radius: 18px;
          background: linear-gradient(135deg, #fb923c 0%, #f43f5e 100%);
          box-shadow: 0 8px 24px rgba(251,146,60,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 32px;
          font-weight: 800;
        ">N</div>
        <div style="
          font-size: 36px;
          font-weight: 800;
          color: white;
          letter-spacing: -0.02em;
          text-align: center;
          line-height: 1.1;
        ">Built for the next billion pixels</div>
        <div class="blurred" style="
          font-size: 16px;
          color: rgba(255,255,255,0.7);
          text-align: center;
          line-height: 1.5;
          max-width: 360px;
        ">One SDF kernel. Sixty frames per second. Pixel-perfect across every iPhone since the A14.</div>
        <div style="
          display: flex;
          gap: 12px;
          margin-top: 8px;
        ">
          <div class="cta" style="
            background: linear-gradient(135deg, #fb923c 0%, #f43f5e 100%);
            color: white;
            padding: 12px 24px;
            border-radius: 12px;
            font-size: 15px;
            font-weight: 600;
            box-shadow: 0 8px 24px rgba(251,146,60,0.4);
          ">Get started</div>
          <div style="
            background: rgba(255,255,255,0.08);
            color: white;
            padding: 12px 24px;
            border-radius: 12px;
            font-size: 15px;
            font-weight: 600;
            border: 1px solid rgba(255,255,255,0.15);
          ">Documentation</div>
        </div>
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

/** A dashboard mockup with header, sidebar stats, three metric cards with
 *  hover lift animation, and a chart placeholder. */
export function sceneDashboard(w: number, h: number): Scene {
  const pad = 16;
  const headerH = 64;
  return compileHtml(`
    <div style="
      width: ${w}px; height: ${h}px;
      background: #0a0a0c;
      padding: ${pad}px;
      display: flex;
      flex-direction: column;
      gap: ${pad}px;
    ">
      <!-- Header -->
      <div style="
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: ${headerH}px;
        padding: 0 20px;
        background: #18181b;
        border-radius: 16px;
        border: 1px solid rgba(255,255,255,0.06);
      ">
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="
            width: 32px; height: 32px;
            border-radius: 8px;
            background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          "></div>
          <div style="font-size: 18px; font-weight: 700; color: white;">Acme Dashboard</div>
        </div>
        <div style="display: flex; gap: 8px; margin-left: auto;">
          <div style="
            background: rgba(34,211,238,0.12);
            color: #67e8f9;
            padding: 6px 12px;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 600;
          ">● Live</div>
          <div style="
            width: 36px; height: 36px;
            border-radius: 50%;
            background: linear-gradient(135deg, #fb923c 0%, #f43f5e 100%);
          "></div>
        </div>
      </div>

      <!-- Stats row -->
      <div style="
        display: flex;
        gap: ${pad}px;
      ">
        <div class="stat" style="
          flex: 1;
          /* Item 1 fix (D): explicit height so the layout engine sizes the
             KPI card to fit its 3-line content (label + big number + delta).
             min-height isn't honored by the layout engine's resolveBoxSize
             (only height + max-height are). Without this explicit height,
             the card collapses to fontSize*lineHeight (~14px) — only the
             label renders, and the chart overlays the missing-number area. */
          height: 110px;
          background: #18181b;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        ">
          <div style="font-size: 12px; color: rgba(255,255,255,0.5); font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em;">Revenue</div>
          <div style="font-size: 28px; font-weight: 800; color: white; margin-top: 4px;">$48.2k</div>
          <div style="font-size: 12px; color: #4ade80; font-weight: 600; margin-top: 4px;">▲ 12.4%</div>
        </div>
        <div class="stat" style="
          flex: 1;
          height: 110px;
          background: #18181b;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        ">
          <div style="font-size: 12px; color: rgba(255,255,255,0.5); font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em;">Users</div>
          <div style="font-size: 28px; font-weight: 800; color: white; margin-top: 4px;">8,492</div>
          <div style="font-size: 12px; color: #4ade80; font-weight: 600; margin-top: 4px;">▲ 4.2%</div>
        </div>
        <div class="stat" style="
          flex: 1;
          height: 110px;
          background: #18181b;
          border: 1px solid rgba(255,255,255,0.06);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        ">
          <div style="font-size: 12px; color: rgba(255,255,255,0.5); font-weight: 500; text-transform: uppercase; letter-spacing: 0.05em;">Conversion</div>
          <div style="font-size: 28px; font-weight: 800; color: white; margin-top: 4px;">3.24%</div>
          <div style="font-size: 12px; color: #f87171; font-weight: 600; margin-top: 4px;">▼ 0.6%</div>
        </div>
      </div>

      <!-- Chart placeholder -->
      <div style="
        flex: 1;
        background: #18181b;
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 16px;
        padding: 24px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        min-height: 200px;
      ">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="font-size: 16px; font-weight: 700; color: white;">Active users</div>
          <div style="font-size: 12px; color: rgba(255,255,255,0.5);">Last 7 days</div>
        </div>
        <div style="
          flex: 1;
          display: flex;
          align-items: flex-end;
          gap: 12px;
          padding-top: 16px;
        ">
          ${[40, 65, 50, 80, 70, 90, 85].map((h, i) => `
            <div style="
              flex: 1;
              height: ${h}%;
              min-height: 24px;
              background: linear-gradient(180deg, #22d3ee 0%, #3b82f6 100%);
              border-radius: 8px 8px 0 0;
              box-shadow: 0 -4px 12px rgba(34,211,238,0.3);
              opacity: ${0.4 + i * 0.08};
            "></div>
          `).join("")}
        </div>
        <div style="display: flex; gap: 12px;">
          ${["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => `
            <div style="flex: 1; text-align: center; font-size: 11px; color: rgba(255,255,255,0.4); font-weight: 500;">${d}</div>
          `).join("")}
        </div>
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

/** Login form with focus rings, real CSS hover states, social auth buttons.
 *  Demonstrates: per-corner border-radius, opacity, transitions, complex
 *  nested flex, gradients. */
export function sceneLogin(w: number, h: number): Scene {
  const pad = 16;
  return compileHtml(`
    <div style="
      width: ${w}px; height: ${h}px;
      background: linear-gradient(180deg, #0a0a0c 0%, #18181b 100%);
      padding: ${pad}px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 100%;
        max-width: 380px;
        background: #18181b;
        border: 1px solid rgba(255,255,255,0.08);
        border-radius: 20px;
        padding: 28px;
        display: flex;
        flex-direction: column;
        gap: 16px;
        box-shadow: 0 32px 64px rgba(0,0,0,0.5);
      ">
        <div style="
          width: 48px; height: 48px;
          border-radius: 14px;
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
          box-shadow: 0 8px 24px rgba(168,85,247,0.4);
          margin-bottom: 4px;
        "></div>
        <div style="font-size: 24px; font-weight: 800; color: white;">Welcome back</div>
        <div style="font-size: 14px; color: rgba(255,255,255,0.6);">Sign in to your account</div>

        <!-- Email field -->
        <div style="display: flex; flex-direction: column; gap: 6px;">
          <div style="font-size: 12px; color: rgba(255,255,255,0.7); font-weight: 500;">Email</div>
          <div style="
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 10px;
            padding: 12px 14px;
            font-size: 14px;
            color: white;
          ">alex@acme.com</div>
        </div>

        <!-- Password field -->
        <div style="display: flex; flex-direction: column; gap: 6px;">
          <div style="display: flex; justify-content: space-between;">
            <div style="font-size: 12px; color: rgba(255,255,255,0.7); font-weight: 500;">Password</div>
            <div style="font-size: 12px; color: #a78bfa; font-weight: 600;">Forgot?</div>
          </div>
          <div style="
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 10px;
            padding: 12px 14px;
            font-size: 14px;
            color: white;
          ">••••••••••</div>
        </div>

        <!-- Sign in button -->
        <div style="
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
          color: white;
          padding: 12px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 700;
          text-align: center;
          margin-top: 8px;
          box-shadow: 0 8px 24px rgba(168,85,247,0.4);
        ">Sign in</div>

        <!-- Divider -->
        <div style="display: flex; align-items: center; gap: 12px; margin: 4px 0;">
          <div style="flex: 1; height: 1px; background: rgba(255,255,255,0.08);"></div>
          <div style="font-size: 11px; color: rgba(255,255,255,0.4); font-weight: 500;">OR CONTINUE WITH</div>
          <div style="flex: 1; height: 1px; background: rgba(255,255,255,0.08);"></div>
        </div>

        <!-- Social buttons -->
        <div style="display: flex; gap: 8px;">
          <div style="
            flex: 1;
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 10px;
            padding: 10px;
            font-size: 13px;
            color: white;
            text-align: center;
            font-weight: 500;
          ">Google</div>
          <div style="
            flex: 1;
            background: rgba(255,255,255,0.04);
            border: 1px solid rgba(255,255,255,0.1);
            border-radius: 10px;
            padding: 10px;
            font-size: 13px;
            color: white;
            text-align: center;
            font-weight: 500;
          ">GitHub</div>
        </div>

        <!-- Footer -->
        <div style="
          font-size: 12px;
          color: rgba(255,255,255,0.5);
          text-align: center;
          margin-top: 4px;
        ">Don't have an account? <span style="color: #a78bfa; font-weight: 600;">Sign up</span></div>
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

/** An animation showcase — three cards each with a different CSS animation:
 *  bounce, slide, and pulse. Demonstrates keyframe parsing + ticking. */
export function sceneAnimations(w: number, h: number): Scene {
  return compileHtml(`
    <style>
      @keyframes float {
        0%, 100% { transform: translateY(0); opacity: 1; }
        50% { transform: translateY(-12px); opacity: 0.95; }
      }
      @keyframes slide {
        0% { transform: translateX(-100px); opacity: 0; }
        20% { opacity: 1; }
        80% { opacity: 1; }
        100% { transform: translateX(100px); opacity: 0; }
      }
      @keyframes pulse {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(1.08); }
      }
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    </style>
    <div style="
      width: ${w}px; height: ${h}px;
      background: #0a0a0c;
      padding: 24px;
      display: flex;
      flex-direction: column;
      gap: 16px;
    ">
      <div style="font-size: 24px; font-weight: 800; color: white;">CSS animations on the GPU</div>
      <div style="font-size: 14px; color: rgba(255,255,255,0.6);">Every keyframe runs through the SDF pipeline — no jank, no janky CSS animations.</div>

      <div style="
        display: flex;
        gap: 16px;
        padding: 32px 0;
        align-items: center;
        justify-content: space-around;
        flex: 1;
        min-height: 200px;
      ">
        <div style="
          animation: float 2s ease-in-out infinite;
          width: 120px;
          height: 120px;
          border-radius: 20px;
          background: linear-gradient(135deg, #fb923c 0%, #f43f5e 100%);
          box-shadow: 0 16px 32px rgba(251,146,60,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 14px;
        ">float</div>

        <div style="
          animation: pulse 1.4s ease-in-out infinite;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          box-shadow: 0 16px 32px rgba(34,211,238,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 14px;
        ">pulse</div>

        <div style="
          animation: spin 3s linear infinite;
          width: 120px;
          height: 120px;
          border-radius: 24px;
          background: linear-gradient(135deg, #a855f7 0%, #ec4899 100%);
          box-shadow: 0 16px 32px rgba(168,85,247,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 14px;
        ">spin</div>

        <div style="
          animation: slide 2.5s ease-in-out infinite;
          width: 120px;
          height: 120px;
          border-radius: 20px;
          background: linear-gradient(135deg, #4ade80 0%, #22d3ee 100%);
          box-shadow: 0 16px 32px rgba(74,222,128,0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 14px;
        ">slide</div>
      </div>

      <div style="
        background: #18181b;
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 12px;
        padding: 16px;
        font-size: 12px;
        color: rgba(255,255,255,0.5);
        font-family: ui-monospace, SFMono-Regular, monospace;
        line-height: 1.6;
      ">
        @keyframes float { 0%, 100% { translateY(0) } 50% { translateY(-12px) } }<br>
        animation: float 2s ease-in-out infinite;
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

/** A CSS Grid demo — 6 cards arranged in a 3-column grid with fr-tracks,
 *  showing the Layer 4 layout engine in action. */
export function sceneGrid(w: number, h: number): Scene {
  const palette = [
    { r: 0.40, g: 0.20, b: 0.95, label: "Purple" },
    { r: 0.95, g: 0.30, b: 0.55, label: "Pink" },
    { r: 0.20, g: 0.65, b: 0.95, label: "Blue" },
    { r: 0.30, g: 0.80, b: 0.50, label: "Green" },
    { r: 0.98, g: 0.74, b: 0.28, label: "Amber" },
    { r: 0.95, g: 0.50, b: 0.20, label: "Orange" },
  ];
  const cells = palette
    .map(
      (p) => `
      <div style="
        background: rgb(${Math.round(p.r * 255)}, ${Math.round(p.g * 255)}, ${Math.round(p.b * 255)});
        border-radius: 16px;
        box-shadow: 0 8px 24px rgba(0,0,0,0.3);
      "></div>`,
    )
    .join("");

  return compileHtml(`
    <div style="
      width: ${w}px;
      height: ${h}px;
      background: linear-gradient(135deg, #1e1b4b 0%, #312e81 100%);
      padding: 32px;
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 20px;
    ">
      ${cells}
  `, { viewportW: w, viewportH: h });
}

/**
 * Scroll-driven animations — exercises scroll-driven keyframe progress plus
 * momentum/inertia on release. The page is taller than the viewport so the
 * user can scroll; nodes with scrollTrack on their animation keyframes are
 * driven by window.scrollY instead of frame dt.
 *
 * What to look for:
 *   - The top "Hero" card fades in as you scroll into the [0, 400px] range.
 *   - The orange "Progress" bar tracks scroll position 1:1 across the full
 *     document height (proves progress mapping works).
 *   - The middle card slides in from the right as you scroll through
 *     [400px, 900px].
 *   - When you flick the scrollbar and let go, the orange bar overshoots
 *     briefly then settles (momentum).
 */
export function sceneScroll(w: number, h: number): Scene {
  const scene = compileHtml(`
    <div style="
      width: ${w}px; min-height: ${h * 3}px;
      background: linear-gradient(180deg, #0f172a 0%, #1e1b4b 50%, #312e81 100%);
      display: flex; flex-direction: column; padding: 24px; gap: 24px;
      color: white; font-family: system-ui, sans-serif;
      box-sizing: border-box;
    ">
      <div style="font-size: 14px; color: rgba(255,255,255,0.6); padding-top: 80px;">
        ↓ Scroll the page ↓
      </div>
      <div id="card-top" style="
        padding: 24px; border-radius: 16px;
        background: rgba(255,255,255,0.06);
        border: 1px solid rgba(255,255,255,0.1);
        font-size: 18px;
      ">
        Scroll-driven keyframes. The orange bar below tracks your scroll
        position; cards on this page fade and slide in as you scroll.
      </div>
      <div id="progress-bar" style="
        position: sticky; top: 24px; height: 32px;
        background: rgba(0,0,0,0.4); border-radius: 16px; overflow: hidden;
        border: 1px solid rgba(255,255,255,0.15);
      ">
        <div style="
          width: 0%; height: 100%;
          background: linear-gradient(90deg, #fb923c, #f43f5e);
          transition: width 0.05s linear;
        "></div>
      </div>
      <div style="height: ${h * 0.8}px;"></div>
      <div id="card-end" style="
        padding: 24px; border-radius: 16px;
        background: rgba(251,146,60,0.15);
        border: 1px solid rgba(251,146,60,0.4);
        font-size: 16px;
      ">
        End of the scrollable demo. The keyframes above were driven entirely
        by window.scrollY — no per-frame dt involved.
      </div>
    </div>
  `, { viewportW: w, viewportH: h });

  // Wire scroll-driven keyframes onto the progress-bar node. We mutate
  // the bar's track to be scroll-driven and animate its width via x-scale.
  // Since rect nodes can't easily animate width directly through AnimatableProps,
  // we use opacity + scaleX to give a clear "alive" feel.
  for (const [, node] of scene.nodes) {
    // Find the "progress-bar" by its position (top area of canvas).
    // Heuristic: a rect node with small h, y near top, large w.
    if (
      node.type === "rect" &&
      node.w > 200 &&
      node.h < 80 &&
      node.y < 200 &&
      node.x < 400
    ) {
      node.animation = {
        duration: 1.0,
        delay: 0,
        iterations: 1,
        direction: "normal",
        fillMode: "none",
        currentIteration: 0,
        elapsedInIteration: 0,
        paused: false,
        scrollTrack: true,
        keyframes: [
          { at: 0, props: { scaleX: 0.4, opacity: 0.6 } },
          { at: 1, props: { scaleX: 1.2, opacity: 1.0 } },
        ],
      };
    }
    // For the bottom card: fade in / slide up across the full scroll.
    if (
      node.type === "rect" &&
      node.w > 200 &&
      node.h > 60 &&
      node.y > 400
    ) {
      node.animation = {
        duration: 1.0,
        delay: 0,
        iterations: 1,
        direction: "normal",
        fillMode: "none",
        currentIteration: 0,
        elapsedInIteration: 0,
        paused: false,
        scrollTrack: true,
        // The rect's "natural" position is set by cssIngest; we modulate
        // scale + opacity to show scroll progress visually.
        keyframes: [
          { at: 0, props: { scaleX: 0.85, scaleY: 0.85, opacity: 0.5 } },
          { at: 1, props: { scaleX: 1.0, scaleY: 1.0, opacity: 1.0 } },
        ],
      };
    }
  }

  return scene;
}

/**
 * Image-rendering showcase — exercises Layer E (raster textures).
 * Three ways to load an image, all in one scene:
 *   1. <img src="data:image/svg+xml;...">    — inline SVG data URL
 *   2. <div background-image: url("data:...")> — same URL via background-image
 *   3. <img src="..."> referencing another URL — repeated to show multiple
 *      textures in flight at once.
 */
export function sceneImages(w: number, h: number): Scene {
  // Three small SVGs encoded as data URLs. They're real rasters from the
  // engine's perspective — getImageTexture uploads them via texImage2D and
  // the rect FS samples them with the textured-fill path.
  const sun =
    "data:image/svg+xml;charset=utf-8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <defs><radialGradient id="g"><stop offset="0" stop-color="#fff7ae"/>
        <stop offset="0.6" stop-color="#fb923c"/><stop offset="1" stop-color="#7c2d12"/></radialGradient></defs>
        <circle cx="50" cy="50" r="44" fill="url(#g)"/>
        <g stroke="#fb923c" stroke-width="6" stroke-linecap="round">
          <line x1="50" y1="6"  x2="50" y2="20"/>
          <line x1="50" y1="80" x2="50" y2="94"/>
          <line x1="6"  y1="50" x2="20" y2="50"/>
          <line x1="80" y1="50" x2="94" y2="50"/>
          <line x1="18" y1="18" x2="28" y2="28"/>
          <line x1="72" y1="72" x2="82" y2="82"/>
          <line x1="18" y1="82" x2="28" y2="72"/>
          <line x1="72" y1="28" x2="82" y2="18"/>
        </g></svg>`,
    );
  const ocean =
    "data:image/svg+xml;charset=utf-8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stop-color="#0ea5e9"/>
          <stop offset="1" stop-color="#082f49"/></linearGradient></defs>
        <rect width="100" height="100" fill="url(#g)"/>
        <path d="M0 60 Q25 50 50 60 T100 60 V100 H0 Z" fill="#0369a1" opacity="0.8"/>
        <path d="M0 75 Q25 65 50 75 T100 75 V100 H0 Z" fill="#075985" opacity="0.8"/>
      </svg>`,
    );
  const leaf =
    "data:image/svg+xml;charset=utf-8," +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
        <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#bbf7d0"/>
          <stop offset="1" stop-color="#15803d"/></linearGradient></defs>
        <path d="M50 8 C 78 30 78 70 50 92 C 22 70 22 30 50 8 Z" fill="url(#g)"/>
        <path d="M50 8 L50 92" stroke="#14532d" stroke-width="2" fill="none"/>
      </svg>`,
    );

  return compileHtml(`
    <div style="
      width: ${w}px; height: ${h}px;
      background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%);
      display: flex; flex-direction: column; align-items: center;
      justify-content: center; padding: 24px; gap: 16px;
      color: white; font-family: system-ui, sans-serif;
    ">
      <div style="font-size: 28px; font-weight: 800;">Image textures</div>
      <div style="display: flex; gap: 24px; align-items: center;">
        <img src="${sun}" style="width: 120px; height: 120px; border-radius: 16px;"/>
        <img src="${ocean}" style="width: 120px; height: 120px; border-radius: 16px;"/>
        <img src="${leaf}" style="width: 120px; height: 120px; border-radius: 16px;"/>
      </div>
      <div style="
        margin-top: 16px; padding: 16px 24px;
        background-image: url('${sun}');
        background-color: rgba(255,255,255,0.04);
        border: 1px solid rgba(255,255,255,0.1);
        border-radius: 12px;
        font-size: 14px; color: rgba(255,255,255,0.85);
        width: 360px; height: 80px;
      ">background-image: url(...) — stretched, rect with imageUrl</div>
      <div style="font-size: 12px; color: rgba(255,255,255,0.6);">
        Three <code>&lt;img&gt;</code> tags + one <code>background-image</code> div
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

// ──────────────────────────────────────────────────────────────────────────
// Instagram-style scenes (dogfood: shows what a Chase-on-file renderer can do)
// ──────────────────────────────────────────────────────────────────────────
//
// sceneIGFeed — a minimal Instagram-shaped feed using only what the engine
// can render today: solid rects + gradients + the box-shadow + radius + filter
// stack. No <img>, no <input>, no actual scroll. The point is to dogfood the
// engine on a recognizable composition and expose the gaps in the renderer
// (text, image nodes, real scroll) the same way a real IG screen would.
//
// Layout: status bar (top) → app bar (logo + DM icon) → story tray (horizontal
// row of gradient avatar discs) → 2 post cards → bottom tab bar. All heights
// are computed against the viewport so it works at portrait-phone aspect.

/** A single IG-shaped post card. Avatar disc + handle row, photo placeholder
 *  (gradient block), action row (heart / comment / share / bookmark), likes,
 *  caption with username prefix. All synthetic — no real text rendering. */
function igPost(handle: string, gradient: string): string {
  return `
    <div style="
      background: #000000;
      border-bottom: 1px solid rgba(255,255,255,0.08);
      display: flex; flex-direction: column;
    ">
      <!-- post header row -->
      <div style="
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 12px;
      ">
        <div style="display: flex; align-items: center; gap: 10px;">
          <div style="
            width: 32px; height: 32px; border-radius: 16px;
            background: ${gradient};
            padding: 2px;
          ">
            <div style="
              width: 28px; height: 28px; border-radius: 14px;
              background: #0a0a0c;
              border: 2px solid #0a0a0c;
            "></div>
          </div>
          <div style="
            font-size: 13px; color: #fafafa; font-weight: 600;
          ">${handle}</div>
        </div>
        <div style="
          width: 18px; height: 18px; border-radius: 9px;
          background: rgba(255,255,255,0.6);
        "></div>
      </div>

      <!-- photo placeholder — a tall gradient block -->
      <div style="
        width: 100%;
        aspect-ratio: 1 / 1;
        background: ${gradient};
      "></div>

      <!-- action row (heart, comment, share, bookmark) -->
      <div style="
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 12px;
      ">
        <div style="display: flex; gap: 14px;">
          <div style="width: 24px; height: 24px; border-radius: 4px; background: #fafafa;"></div>
          <div style="width: 24px; height: 24px; border-radius: 4px; background: #fafafa;"></div>
          <div style="width: 24px; height: 24px; border-radius: 4px; background: #fafafa;"></div>
        </div>
        <div style="width: 24px; height: 24px; border-radius: 4px; background: #fafafa;"></div>
      </div>

      <!-- likes -->
      <div style="
        padding: 0 12px 6px;
      ">
        <div style="
          width: 88px; height: 10px;
          background: rgba(255,255,255,0.85);
          border-radius: 2px;
        "></div>
      </div>

      <!-- caption — username + 2 lines of placeholder bars -->
      <div style="
        padding: 0 12px 12px;
        display: flex; flex-direction: column; gap: 6px;
      ">
        <div style="
          width: 60%; height: 10px;
          background: rgba(255,255,255,0.85);
          border-radius: 2px;
        "></div>
        <div style="display: flex; flex-direction: column; gap: 4px; padding-top: 4px;">
          <div style="
            width: 100%; height: 8px;
            background: rgba(255,255,255,0.55);
            border-radius: 2px;
          "></div>
          <div style="
            width: 78%; height: 8px;
            background: rgba(255,255,255,0.55);
            border-radius: 2px;
          "></div>
        </div>
      </div>
    </div>
  `;
}

/** Build the IG-shaped feed: app bar → stories → 2 posts → tab bar.
 *  Everything is solid rectangles + gradients; no text rendering, no images,
 *  no real scroll. Width = viewport, height = viewport, but the inner flex
 *  column can extend if needed. */
export function sceneIGFeed(w: number, h: number): Scene {
  const story1 = "linear-gradient(135deg,#f9ce34 0%,#ee2a7b 50%,#6228d7 100%)"; // IG gradient 1
  const story2 = "linear-gradient(135deg,#fcb045 0%,#fd1d1d 100%)";
  const story3 = "linear-gradient(135deg,#833ab4 0%,#fd1d1d 50%,#fcb045 100%)";
  const story4 = "linear-gradient(135deg,#405de6 0%,#5851db 50%,#833ab4 100%)";
  const story5 = "linear-gradient(135deg,#5851db 0%,#833ab4 50%,#c13584 100%)";
  const story6 = "linear-gradient(135deg,#fd1d1d 0%,#833ab4 100%)";

  const post1Photo = "linear-gradient(135deg,#0f172a 0%,#312e81 50%,#831843 100%)";
  const post2Photo = "linear-gradient(135deg,#064e3b 0%,#134e4a 50%,#831843 100%)";

  return compileHtml(`
    <div style="
      width: ${w}px;
      min-height: ${h}px;
      background: #0a0a0c;
      display: flex; flex-direction: column;
      color: #fafafa;
    ">
      <!-- status bar -->
      <div style="
        height: 24px; padding: 8px 16px 0;
        display: flex; align-items: center; justify-content: space-between;
      ">
        <div style="
          width: 56px; height: 10px;
          background: rgba(255,255,255,0.85);
          border-radius: 2px;
        "></div>
        <div style="display: flex; gap: 6px;">
          <div style="width: 14px; height: 10px; background: rgba(255,255,255,0.85); border-radius: 2px;"></div>
          <div style="width: 14px; height: 10px; background: rgba(255,255,255,0.85); border-radius: 2px;"></div>
          <div style="width: 18px; height: 10px; background: rgba(255,255,255,0.85); border-radius: 2px;"></div>
        </div>
      </div>

      <!-- app bar: logo + DM icon -->
      <div style="
        display: flex; align-items: center; justify-content: space-between;
        padding: 10px 14px;
        border-bottom: 1px solid rgba(255,255,255,0.06);
      ">
        <div style="
          width: 96px; height: 22px;
          background: linear-gradient(90deg,#f9ce34,#ee2a7b,#6228d7);
          border-radius: 4px;
        "></div>
        <div style="display: flex; gap: 16px;">
          <div style="
            width: 22px; height: 22px;
            background: rgba(255,255,255,0.85);
            border-radius: 4px;
          "></div>
          <div style="
            width: 22px; height: 22px;
            background: rgba(255,255,255,0.85);
            border-radius: 4px;
          "></div>
        </div>
      </div>

      <!-- story tray (horizontal scroll, but rendered as a fixed row) -->
      <div style="
        display: flex; gap: 14px;
        padding: 14px 12px;
        border-bottom: 1px solid rgba(255,255,255,0.06);
        overflow: hidden;
      ">
        ${[
          { g: story1, t: "You" },
          { g: story2, t: "ada" },
          { g: story3, t: "kai" },
          { g: story4, t: "roy" },
          { g: story5, t: "liv" },
          { g: story6, t: "ben" },
        ]
          .map(
            (s) => `
          <div style="
            display: flex; flex-direction: column; align-items: center;
            gap: 4px; min-width: 60px;
          ">
            <div style="
              width: 64px; height: 64px; border-radius: 32px;
              background: ${s.g};
              padding: 2px;
            ">
              <div style="
                width: 60px; height: 60px; border-radius: 30px;
                background: linear-gradient(135deg,#27272a,#3f3f46);
                border: 2px solid #0a0a0c;
              "></div>
            </div>
            <div style="
              width: 36px; height: 8px;
              background: rgba(255,255,255,0.7);
              border-radius: 2px;
            "></div>
          </div>
        `,
          )
          .join("")}
      </div>

      ${igPost("ada.heath", post1Photo)}
      ${igPost("kai.morrow", post2Photo)}

      <!-- bottom tab bar -->
      <div style="
        display: flex; align-items: center; justify-content: space-around;
        height: 56px;
        background: #0a0a0c;
        border-top: 1px solid rgba(255,255,255,0.08);
        padding: 0 12px;
      ">
        <div style="width: 22px; height: 22px; background: #fafafa; border-radius: 4px;"></div>
        <div style="width: 22px; height: 22px; background: rgba(255,255,255,0.5); border-radius: 4px;"></div>
        <div style="
          width: 36px; height: 36px;
          background: linear-gradient(135deg,#f9ce34,#ee2a7b,#6228d7);
          border-radius: 8px;
        "></div>
        <div style="width: 22px; height: 22px; background: rgba(255,255,255,0.5); border-radius: 4px;"></div>
        <div style="width: 22px; height: 22px; background: rgba(255,255,255,0.5); border-radius: 4px;"></div>
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

/** Public scene registry for main.ts. */
export const PAGE_SCENES: Record<string, (w: number, h: number) => Scene> = {
  hero: sceneHero,
  dashboard: sceneDashboard,
  login: sceneLogin,
  "ig-login": sceneLogin,   // alias used by the IG demo set (slug-style)
  "ig-feed": sceneIGFeed,   // IG-shaped feed scene
  animations: sceneAnimations,
  grid: sceneGrid,
  paths: scenePaths,
  images: sceneImages,
  scroll: sceneScroll,
};

/**
 * SVG path-rendering showcase — exercises Layer H (paths).
 *
 * Shows:
 *   - Solid-fill paths (heart, triangle, star, circle)
 *   - Stroked paths (heart outline, sine wave)
 *   - Cubic and quadratic Bezier curves
 *   - Paths colored with the CSS `fill` attribute
 *   - Paths animated with CSS keyframes
 */
export function scenePaths(w: number, h: number): Scene {
  // Common icon-sized path data. All in 24x24 viewBox (matches Feather/Lucide).
  const heart = "M12 21s-7-4.5-9-9.5C1.5 7 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 22.5 7 21 11.5c-2 5-9 9.5-9 9.5z";
  const star = "M12 2 L15 9 L22 9 L17 14 L19 22 L12 18 L5 22 L7 14 L2 9 L9 9 Z";
  const triangle = "M12 3 L22 21 L2 21 Z";
  const bolt = "M13 2 L4 14 L11 14 L9 22 L20 10 L13 10 Z";
  const wave = "M2 12 C 6 2, 10 22, 14 12 S 22 2, 22 12";
  return compileHtml(`
    <style>
      .stage {
        width: ${w}px; height: ${h}px;
        background: linear-gradient(135deg, #0c0a1e 0%, #1e1b4b 100%);
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        padding: 40px; gap: 32px;
        color: white; font-family: -apple-system, BlinkMacSystemFont, sans-serif;
      }
      .row { display: flex; gap: 24px; align-items: center; }
      .icon { width: 56px; height: 56px; }
      .big { width: 96px; height: 96px; }
      .label { font-size: 14px; opacity: 0.7; text-align: center; margin-top: 8px; }
      .col { display: flex; flex-direction: column; align-items: center; }
      @keyframes spin-slow { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      @keyframes pulse-scale { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.15); } }
      .spinning { animation: spin-slow 6s linear infinite; transform-origin: center; }
      .pulsing  { animation: pulse-scale 1.2s ease-in-out infinite; transform-origin: center; }
      .title { font-size: 22px; font-weight: 700; letter-spacing: -0.02em; }
      .sub { font-size: 13px; opacity: 0.55; }
    </style>
    <div class="stage">
      <div class="col" style="gap:6px">
        <div class="title">SVG Path Rendering</div>
        <div class="sub">Layer H — solid + stroked Bezier paths rasterized to the rect pipeline</div>
      </div>
      <div class="row">
        <div class="col">
          <svg class="icon" viewBox="0 0 24 24" fill="#f43f5e"><path d="${heart}"/></svg>
          <div class="label">solid fill</div>
        </div>
        <div class="col">
          <svg class="icon" viewBox="0 0 24 24" fill="#fb923c"><path d="${star}"/></svg>
          <div class="label">solid</div>
        </div>
        <div class="col">
          <svg class="icon" viewBox="0 0 24 24" fill="#22d3ee"><path d="${triangle}"/></svg>
          <div class="label">triangle</div>
        </div>
        <div class="col">
          <svg class="icon pulsing" viewBox="0 0 24 24" fill="#facc15"><path d="${bolt}"/></svg>
          <div class="label">keyframed</div>
        </div>
        <div class="col">
          <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" stroke-width="2"><path d="${heart}"/></svg>
          <div class="label">stroked</div>
        </div>
        <div class="col">
          <svg class="big spinning" viewBox="0 0 24 24" fill="none" stroke="#34d399" stroke-width="1.5"><path d="${wave}"/></svg>
          <div class="label">cubic + quadratic</div>
        </div>
      </div>
    </div>
  `, { viewportW: w, viewportH: h });
}

// Keep createScene import used.
void createScene;

// =============================================================================
// Product page demo — the Cupshe Blue Striped Mini Dress.
//
// Built with compileHtml so it exercises the full NSPP pipeline: SDF rects,
// CSS layout (flex/grid), image-texture rendering, gradients, text.
// The 10 dress photos live in /static/products/cupshe-blue-striped-dress/.
//
// Animation: each image node has an update: (n, t, dt) hook that swaps
// which image is "active" by setting n.opacity. The 8-step sequence runs
// from t=0 to t=1.3s. T=0 is the initial state, t=1.3+ is the end state.
// =============================================================================

const PRODUCT_STEP_BREAKS = [0.00, 0.35, 0.50, 0.65, 0.80, 0.95, 1.10, 1.30, 999];

/** Build a NSPP scene that renders the Cupshe product page using the engine's
 *  full pipeline — compileHtml + image textures + SDF + CSS. */
export function sceneProductPage(w: number, h: number): Scene {
  // Defensive: __loadScene may be called with w=0, h=0 if the canvas is
  // hidden. Use sensible defaults so the layout still works.
  if (!w || !h) { w = 1440; h = 900; }

  // 10 image URLs to /static/products/cupshe-blue-striped-dress/
  const imgs = Array.from({ length: 10 }, (_, i) =>
    `/products/cupshe-blue-striped-dress/view-${String(i + 1).padStart(2, "0")}.jpg`
  );

  // Compute pixel-perfect column widths for the inline styles. Flex layout
  // with inline `width:` works reliably (css-block % widths get auto-overridden
  // by the engine's grid-auto-size heuristic when content overflows).
  const LEFT_W = Math.floor(w * 0.6);
  const RIGHT_W = w - LEFT_W;
  const PAD = 20;
  const IMG_W = LEFT_W - PAD * 2;
  const IMG_H = h - 140;

  // We use a SINGLE image node for the main viewer (so the layout engine
  // doesn't lay out 10 absolute children as flex siblings). The texture
  // URL is swapped per frame via the update hook.
  const MAIN_IMG = imgs[0];

  const scene = compileHtml(`
    <div class="pp" style="
      width: ${w}px; height: ${h}px;
      display: flex;
      box-sizing: border-box;
      background: #fff;
      color: #1a1a1a;
      font-family: -apple-system, 'Helvetica Neue', sans-serif;
    ">
      <!-- LEFT COLUMN: main image stage + thumbnails row -->
      <div style="
        width: ${LEFT_W}px; height: ${h}px;
        background: #f5f5f5;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: ${PAD}px;
        box-sizing: border-box;
      ">
        <div style="position: absolute; top: 16px; left: 16px; background: #c2392f; color: #fff; padding: 4px 10px; font-size: 10px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; border-radius: 2px;">-30%</div>
        <!-- Single main image — its src is swapped per-frame by the update hook -->
        <img src="${MAIN_IMG}" style="width: ${IMG_W}px; height: ${IMG_H - 70}px; object-fit: contain; box-shadow: 0 12px 32px rgba(0,0,0,0.15);"/>
        <!-- Thumbnail row at bottom -->
        <div style="
          display: flex;
          gap: 6px;
          padding: 6px 10px;
          background: rgba(255,255,255,0.9);
          border-radius: 10px;
          margin-top: 8px;
        ">
          ${imgs.map((src, i) =>
            `<img src="${src}" style="width: 32px; height: 44px; object-fit: cover; border-radius: 3px; ${i === 0 ? "border: 2px solid #1a1a1a;" : "border: 2px solid transparent;"}"/>`
          ).join("")}
        </div>
      </div>

      <!-- RIGHT COLUMN: product info -->
      <div style="
        width: ${RIGHT_W}px; height: ${h}px;
        padding: ${PAD}px;
        box-sizing: border-box;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        gap: 10px;
      ">
        <div style="font-size: 10px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; color: #1a1a1a;">Cupshe</div>
        <h1 style="font-size: 17px; font-weight: 600; line-height: 1.2; margin: 0; color: #1a1a1a;">Striped V-Neck Flutter Sleeve Mini Dress</h1>
        <div style="display: flex; align-items: center; gap: 8px; font-size: 11px; color: #6b6b6b;">
          <span style="color: #ffb400; letter-spacing: 1px;">★★★★★</span>
          <span>4.8 · 1,247 reviews</span>
        </div>
        <div style="display: flex; align-items: baseline; gap: 10px;">
          <span style="font-size: 22px; font-weight: 700; color: #c2392f;">$38.50</span>
          <span style="font-size: 14px; color: #999; text-decoration: line-through;">$55.00</span>
          <span style="background: #c2392f; color: #fff; padding: 2px 6px; font-size: 10px; font-weight: 700; border-radius: 2px;">SAVE 30%</span>
        </div>
        <div style="font-size: 10px; line-height: 1.45; color: #4a4a4a;">Effortless summer style. Soft cotton-blend seersucker in a vertical ticking stripe. Flutter cap sleeves, V-neckline, button-front, fitted waist, A-line mini skirt. Lined.</div>
        <div style="font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; margin-top: 4px;">Color · Cream / White Stripe</div>
        <div style="display: flex; gap: 8px;">
          <div style="width: 26px; height: 26px; border-radius: 50%; background: linear-gradient(180deg,#f5e8c0 50%,#fff 50%); border: 2px solid #1a1a1a;"></div>
          <div style="width: 26px; height: 26px; border-radius: 50%; background: linear-gradient(180deg,#5b7fa8 50%,#fff 50%); border: 1px solid #d4d4d4;"></div>
          <div style="width: 26px; height: 26px; border-radius: 50%; background: linear-gradient(180deg,#1a1a1a 50%,#fff 50%); border: 1px solid #d4d4d4;"></div>
          <div style="width: 26px; height: 26px; border-radius: 50%; background: linear-gradient(180deg,#c2392f 50%,#fff 50%); border: 1px solid #d4d4d4;"></div>
        </div>
        <div style="font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase;">Size</div>
        <div style="display: flex; gap: 6px; flex-wrap: wrap;">
          <div style="min-width: 32px; padding: 6px 8px; border: 1px solid #d4d4d4; border-radius: 4px; text-align: center; font-size: 11px; font-weight: 500;">XS</div>
          <div style="min-width: 32px; padding: 6px 8px; border: 1px solid #d4d4d4; border-radius: 4px; text-align: center; font-size: 11px; font-weight: 500;">S</div>
          <div style="min-width: 32px; padding: 6px 8px; border: 1px solid #1a1a1a; border-radius: 4px; text-align: center; font-size: 11px; font-weight: 500; background: #1a1a1a; color: #fff;">M</div>
          <div style="min-width: 32px; padding: 6px 8px; border: 1px solid #d4d4d4; border-radius: 4px; text-align: center; font-size: 11px; font-weight: 500;">L</div>
          <div style="min-width: 32px; padding: 6px 8px; border: 1px solid #d4d4d4; border-radius: 4px; text-align: center; font-size: 11px; font-weight: 500;">XL</div>
          <div style="min-width: 32px; padding: 6px 8px; border: 1px solid #d4d4d4; border-radius: 4px; text-align: center; font-size: 11px; font-weight: 500;">XXL</div>
        </div>
        <div style="display: flex; gap: 8px; margin-top: 4px;">
          <div style="display: flex; align-items: center; border: 1px solid #d4d4d4; border-radius: 4px;">
            <div style="width: 30px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 14px;">−</div>
            <div style="width: 30px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 12px;">1</div>
            <div style="width: 30px; height: 32px; display: flex; align-items: center; justify-content: center; font-size: 14px;">+</div>
          </div>
          <div style="flex: 1; height: 32px; background: #1a1a1a; color: #fff; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;">Add to Bag</div>
          <div style="width: 32px; height: 32px; background: #fff; border: 1px solid #d4d4d4; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 14px;">♡</div>
        </div>
        <div style="margin-top: auto; padding-top: 10px; border-top: 1px solid #ebebeb; display: flex; flex-direction: column; gap: 2px; font-size: 10px; color: #6b6b6b;">
          <div><b style="color: #1a1a1a;">SKU</b> &nbsp; CAA05B4H006UC</div>
          <div><b style="color: #1a1a1a;">Ships from</b> &nbsp; California, USA</div>
          <div><b style="color: #1a1a1a;">Delivery</b> &nbsp; 3-5 business days</div>
          <div><b style="color: #1a1a1a;">Returns</b> &nbsp; Free within 30 days</div>
        </div>
      </div>
    </div>
  `, { viewportW: w, viewportH: h });

  // 10 distinct Ken Burns animations — each image gets its own zoom+pan
  // choreography so consecutive thumbnails feel alive rather than a uniform
  // pulse.
  type KenBurns = { sx0: number; sy0: number; sx1: number; sy1: number };
  const KEN_BURNS: KenBurns[] = [
    { sx0: 1.00, sy0: 1.00, sx1: 1.12, sy1: 1.08 },
    { sx0: 1.12, sy0: 1.08, sx1: 1.04, sy1: 1.04 },
    { sx0: 1.04, sy0: 1.04, sx1: 1.14, sy1: 1.08 },
    { sx0: 1.10, sy0: 1.14, sx1: 1.06, sy1: 1.02 },
    { sx0: 1.06, sy0: 1.02, sx1: 1.12, sy1: 1.12 },
    { sx0: 1.12, sy0: 1.12, sx1: 1.04, sy1: 1.04 },
    { sx0: 1.00, sy0: 1.00, sx1: 1.14, sy1: 1.14 },
    { sx0: 1.14, sy0: 1.14, sx1: 1.06, sy1: 1.06 },
    { sx0: 1.04, sy0: 1.10, sx1: 1.12, sy1: 1.06 },
    { sx0: 1.12, sy0: 1.06, sx1: 1.06, sy1: 1.10 },
  ];

  // Gentle Ken Burns scale applied continuously to the currently-selected
  // image. The image itself does NOT auto-cycle — it only changes when the
  // user clicks a thumbnail. Each click resets `cycleStartT` so the breath
  // cycle begins from sx0/sy0 on the new image.
  const KB = [
    { sx0: 1.00, sy0: 1.00, sx1: 1.06, sy1: 1.05 },
    { sx0: 1.06, sy0: 1.05, sx1: 1.02, sy1: 1.02 },
    { sx0: 1.02, sy0: 1.02, sx1: 1.07, sy1: 1.04 },
    { sx0: 1.07, sy0: 1.04, sx1: 1.03, sy1: 1.01 },
    { sx0: 1.03, sy0: 1.01, sx1: 1.06, sy1: 1.06 },
    { sx0: 1.06, sy0: 1.06, sx1: 1.02, sy1: 1.02 },
    { sx0: 1.00, sy0: 1.00, sx1: 1.07, sy1: 1.07 },
    { sx0: 1.07, sy0: 1.07, sx1: 1.03, sy1: 1.03 },
    { sx0: 1.03, sy0: 1.05, sx1: 1.06, sy1: 1.03 },
    { sx0: 1.06, sy0: 1.03, sx1: 1.03, sy1: 1.05 },
  ];

  // Manual image picking. currentImageIdx is the source of truth — the
  // main viewer only swaps when a thumbnail is tapped. The Ken Burns scale
  // animation is continuous on the currently-selected image.
  let currentImageIdx = 0;
  let cycleStartT = 0; // engine time when the current image became active

  // Find image nodes and wire their tap + update hooks.
  // Image nodes: index 0 is the MAIN viewer, indices 1-10 are thumbnails.
  const allNodes = Array.from(scene.nodes.values());
  const imageNodes = allNodes.filter((n) => n.type === "image");
  const mainImg = imageNodes[0];
  const thumbnailNodes = imageNodes.slice(1);

  // Find the swatch nodes. We tag them at compile time by walking the
  // scene's nodes for those that ended up in the right column with a width
  // of 26px and height 26px (matches our .pp-swatch CSS). It's a heuristic
  // but reliable since nothing else in the scene uses that exact size.
  const swatchNodes = allNodes.filter((n) =>
    n.type === "rect" && Math.round(n.w) === 26 && Math.round(n.h) === 26
  );

  // Color variants — each swatch maps to a specific image index that shows
  // the dress in that color. The Cupshe product photos include two colorways:
  //   - Cream/White (view-01, view-02 — front & back)
  //   - Blue       (view-03 through view-10 — various poses)
  // There's no real black or red photo in the set, so for those we use
  // a CSS filter (hue-rotate) on the cream/white photo to fake the variant.
  // For cream and blue we just swap the image.
  const SWATCH_IMAGE_IDX = [0, 4, 0, 0];  // cream, blue, black, red
  const SWATCH_HUE_RAD = [
    0,                  // 0: cream — no filter
    0,                  // 1: blue — use blue photo (no filter)
    Math.PI,            // 2: black — invert cream photo
    -Math.PI / 6,       // 3: red — hue-rotate cream photo
  ];
  const SWATCH_INVERT = [0, 0, 1, 0];  // only the "black" swatch inverts

  // Current color swatch selection. Default 0 = cream/white.
  let currentSwatchIdx = 0;
  // The main viewer displays whatever image is at this index. Both
  // swatch clicks and thumbnail clicks write to it. The update hook
  // just reads — it never overwrites — so user picks persist.
  let displayedImageIdx = 0;

  // Wire swatch taps → set the active image (or filter for fake variants)
  // and update the active visual indicator.
  for (let i = 0; i < swatchNodes.length && i < 4; i++) {
    const sn = swatchNodes[i];
    const idx = i;
    sn.update = (n) => {
      const isActive = idx === currentSwatchIdx;
      n.stroke.a = isActive ? 1 : 0;
      n.strokeWidth = isActive ? 2 : 1;
      n.borderColor = isActive
        ? { r: 0.1, g: 0.1, b: 0.1, a: 1 }
        : { r: 0.1, g: 0.1, b: 0.1, a: 0.5 };
    };
    // (Swatch onTap isn't useful because the swatch node might not be
    // hit-testable on its own — the onPointerDown hook below does
    // hit-testing by position.)
  }

  // Wire thumbnail taps → swap the main image and restart the breath cycle.
  for (let i = 0; i < thumbnailNodes.length && i < imgs.length; i++) {
    const tn = thumbnailNodes[i];
    const idx = i;
    tn.onTap = () => {
      if (displayedImageIdx === idx) return; // already showing
      displayedImageIdx = idx;
      cycleStartT = -1; // sentinel: restart on next tick
      // Force the main image to swap immediately.
      if (mainImg) mainImg.imageUrl = imgs[idx];
    };
    // Active-thumbnail visual indicator: the picked one gets a thicker
    // visible border in NSPP orange.
    tn.update = (n) => {
      const isActive = idx === displayedImageIdx;
      n.stroke.a = isActive ? 1 : 0;
      n.strokeWidth = isActive ? 2 : 1;
      n.borderColor = isActive
        ? { r: 0.98, g: 0.57, b: 0.24, a: 1 }
        : { r: 0.1, g: 0.1, b: 0.1, a: 0.5 };
    };
  }

  if (mainImg) {
    mainImg.update = (n, t /* seconds */) => {
      // Anchor the breath cycle to when the current image became active. The
      // first time we see the scene (t==0), set the anchor to now. After a
      // tap, the onTap handler sets cycleStartT=-1 so we re-anchor below.
      if (cycleStartT < 0 || (currentImageIdx === 0 && cycleStartT === 0)) {
        cycleStartT = t;
      }
      const elapsed = Math.max(0, t - cycleStartT);
      // 6-second breath cycle: slow sine through sx0→sx1→sx0 with easing.
      const kb = KB[currentImageIdx];
      const breath = Math.sin((elapsed / 6.0) * Math.PI * 2) * 0.5 + 0.5;
      const e = breath * breath * (3 - 2 * breath); // smoothstep
      n.scaleX = kb.sx0 + (kb.sx1 - kb.sx0) * e;
      n.scaleY = kb.sy0 + (kb.sy1 - kb.sy0) * e;
      // Image + filter: cream/blue use the actual photo; black/red fake the
      // variant via hue-rotate / invert on the cream photo.
      const targetIdx = SWATCH_IMAGE_IDX[currentSwatchIdx] ?? 0;
      n.imageUrl = imgs[targetIdx];
      const rad = SWATCH_HUE_RAD[currentSwatchIdx] ?? 0;
      const inv = SWATCH_INVERT[currentSwatchIdx] ?? 0;
      n.filter = { ...n.filter, hueRotate: rad, invert: inv };
    };
  }

  // Per-scene pointer hook: in case the generic tap-routing misses (the
  // generic system relies on hitTest, which can fail for very small nodes).
  // This is a backup that hit-tests the thumbnail row and swatches directly.
  scene.onPointerDown = (x, y) => {
    // Thumbnail row.
    if (thumbnailNodes.length) {
      for (let i = 0; i < thumbnailNodes.length && i < imgs.length; i++) {
        const tn = thumbnailNodes[i];
        if (x >= tn.x && x <= tn.x + tn.w && y >= tn.y && y <= tn.y + tn.h) {
          if (currentImageIdx === i) return true;
          currentImageIdx = i;
          cycleStartT = -1;
          if (mainImg) mainImg.imageUrl = imgs[i];
          return true;
        }
      }
    }
    // Color swatch row.
    if (swatchNodes.length) {
      for (let i = 0; i < swatchNodes.length && i < 4; i++) {
        const sn = swatchNodes[i];
        if (x >= sn.x && x <= sn.x + sn.w && y >= sn.y && y <= sn.y + sn.h) {
          currentSwatchIdx = i;
          return true;
        }
      }
    }
    return false;
  };

  return scene;
}
// Register the new scene.
if (!("product" in PAGE_SCENES)) {
  (PAGE_SCENES as Record<string, (w: number, h: number) => Scene>)["product"] = sceneProductPage;
}


